/**
 * M Cart Drawer - Enhanced cart drawer functionality
 * Handles free samples, free sachets, and free shipping bar
 */

class MCartDrawer {
  constructor(element) {
    this.element = element || document.querySelector('.m-cart-drawer__inner') || document.querySelector('#MCartDrawer');
    this.init();
  }

  /**
   * Lightweight debug logger for development.
   * Logs only in theme editor (Shopify.designMode) or when m_cart_debug=1 is in the URL.
   */
  debugLog(...args) {
    try {
      const search = window.location.search || '';
      const hasDebugParam = search.includes('m_cart_debug=1');
      const inDesignMode = window.Shopify && window.Shopify.designMode;

      if ((inDesignMode || hasDebugParam) && window.console && console.log) {
        console.log('[MCartDrawer]', ...args);
      }
    } catch (e) {
      // Swallow any unexpected errors to avoid impacting customers
    }
  }

  init() {
    this.setupFreeSampleButtons();
    this.setupCartDrawerAccordions();
    this.setupRecentlyViewed();
    this.setupRecentlyViewedAdd();
    this.updateFreeShippingBar();
    this.setupCartUpdateListener();
    this.setupCloseButton();
    this.setupRedeemButtonClose();
    this.removeDuplicateLoyaltyElements();
  }

  /**
   * Close cart when Redeem is clicked so the Love Loyalty modal has full space.
   * Otherwise both stay open and overlap (modal on top of cart).
   */
  setupRedeemButtonClose() {
    const dialog = document.querySelector('custom-dialog[data-name="m-cart-drawer"]');
    if (!dialog) return;

    dialog.addEventListener(
      'click',
      (e) => {
        if (e.target.closest('.ll__rewards-button')) {
          document.dispatchEvent(new CustomEvent('dialog:force-close:m-cart-drawer'));
        }
      },
      true
    );
  }

  /**
   * Smooth open/close animation for free sample & free sachets accordions (same as Key Ingredients)
   */
  setupCartDrawerAccordions() {
    const ANIMATION_DURATION = 300;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const details = this.element?.querySelectorAll(
      '.m-cart-drawer__free-sample-accordion, .m-cart-drawer__free-sachets-accordion'
    );
    if (!details || details.length === 0) return;

    details.forEach((detail) => {
      const summary = detail.querySelector('summary');
      const content = detail.querySelector('[data-content]');
      if (!summary || !content) return;
      if (detail.dataset.accordionAnimated === 'true') return;

      detail.dataset.accordionAnimated = 'true';
      detail._accordion = { summary, content, animation: null, isClosing: false, isExpanding: false };

      const collapse = (el) => {
        const { summary: s, content: c, animation } = el._accordion;
        if (!c) return;
        el._accordion.isClosing = true;
        el.style.overflow = 'hidden';
        const startHeight = `${el.offsetHeight}px`;
        const endHeight = `${s.offsetHeight}px`;
        if (animation) animation.cancel();
        if (!prefersReducedMotion.matches) {
          el._accordion.animation = el.animate(
            { height: [startHeight, endHeight] },
            { duration: ANIMATION_DURATION, easing: 'ease' }
          );
          el._accordion.animation.onfinish = () => onFinish(el, false);
          el._accordion.animation.oncancel = () => (el._accordion.isClosing = false);
        } else {
          onFinish(el, false);
        }
      };

      const expand = (el) => {
        const { summary: s, content: c, animation } = el._accordion;
        if (!c) return;
        el.style.overflow = 'hidden';
        el.style.height = `${el.offsetHeight}px`;
        el.open = true;
        requestAnimationFrame(() => {
          el._accordion.isExpanding = true;
          const startHeight = el.style.height;
          const endHeight = `${s.offsetHeight + c.offsetHeight}px`;
          if (animation) el._accordion.animation?.cancel();
          if (!prefersReducedMotion.matches) {
            el._accordion.animation = el.animate(
              { height: [startHeight, endHeight] },
              { duration: ANIMATION_DURATION, easing: 'ease' }
            );
            el._accordion.animation.onfinish = () => onFinish(el, true);
            el._accordion.animation.oncancel = () => (el._accordion.isExpanding = false);
          } else {
            onFinish(el, true);
          }
        });
      };

      const onFinish = (el, open) => {
        el.open = open;
        el._accordion.animation = null;
        el._accordion.isClosing = false;
        el._accordion.isExpanding = false;
        el.style.height = '';
        el.style.overflow = '';
      };

      summary.addEventListener('click', (e) => {
        e.preventDefault();
        const { isClosing, isExpanding } = detail._accordion;
        if (isClosing || isExpanding) return;
        if (detail.open) {
          collapse(detail);
        } else {
          expand(detail);
        }
      });
    });
  }

  removeDuplicateLoyaltyElements() {
    // Remove duplicate loyalty program elements from cart drawer
    // Keep only the first one (in our placeholder div)
    const footer = this.element?.querySelector('[data-cart-footer]') || document.querySelector('[data-cart-footer]');
    if (!footer) return;

    const loyaltyElements = footer.querySelectorAll('[class*="ll__cart"]');
    if (loyaltyElements.length > 1) {
      // Keep the first one, remove the rest
      for (let i = 1; i < loyaltyElements.length; i++) {
        loyaltyElements[i].remove();
      }
    }

    // Fix login links to return to current page with drawer hash instead of /cart
    // Support both legacy /account/login and new Customer Accounts customer_authentication/redirect
    const loginLinks = footer.querySelectorAll('[class*="ll__cart"] a[href*="/account/login"], [class*="ll__cart"] a[href*="customer_authentication"]');
    loginLinks.forEach((link) => {
      const currentUrl = window.location.pathname + window.location.search;
      const returnUrl = encodeURIComponent(currentUrl + '#open-drawer');
      const href = link.getAttribute('href') || '';
      if (href.includes('customer_authentication')) {
        try {
          const url = new URL(link.href);
          url.searchParams.set('return_url', currentUrl + '#open-drawer');
          link.href = url.toString();
        } catch {
          link.href = link.href.replace(/\?return_url=[^&]+|&return_url=[^&]+/, '') + (link.href.includes('?') ? '&' : '?') + 'return_url=' + encodeURIComponent(currentUrl + '#open-drawer');
        }
      } else {
        link.href = `/account/login?return_url=${returnUrl}`;
      }
    });


    // Replace SVG icon with loyalty program icon div (to match PDP)
    // When logged in: replace SVG in .ll__point-balance
    // When logged out: wrap icon + span in .ll__point-balance (groups them, prevents centering)
    loyaltyElements.forEach((element) => {
      const pointBalance = element.querySelector('.ll__point-balance');
      if (pointBalance) {
        const svgIcon = pointBalance.querySelector('svg');
        if (svgIcon) {
          const iconDiv = document.createElement('div');
          iconDiv.className = 'loyalty-program-icon';
          svgIcon.parentNode.replaceChild(iconDiv, svgIcon);
        }
      } else {
        const container = element.closest('.ll__container') || (element.classList.contains('ll__container') ? element : element.querySelector('.ll__container'));
        const target = container || element;
        const firstSpan = target.querySelector('span');
        if (firstSpan && !target.querySelector('.ll__point-balance')) {
          const iconDiv = document.createElement('div');
          iconDiv.className = 'loyalty-program-icon';
          const pointBalanceWrapper = document.createElement('div');
          pointBalanceWrapper.className = 'll__point-balance';
          target.insertBefore(pointBalanceWrapper, firstSpan);
          pointBalanceWrapper.appendChild(iconDiv);
          pointBalanceWrapper.appendChild(firstSpan);
        }
      }
    });
  }

  setupCloseButton() {
    // Use event delegation to handle close button clicks
    // This ensures it works even after cart-items refreshes
    const dialog = document.querySelector('custom-dialog[data-name="m-cart-drawer"]');
    if (dialog) {
      // Remove any existing listener to avoid duplicates
      dialog.removeEventListener('click', this.handleCloseClick);
      this.handleCloseClick = (e) => {
        const closeButton = e.target.closest('[data-close]');
        if (closeButton) {
          e.preventDefault();
          e.stopPropagation();
          // Dispatch the close event that CustomDialog listens for
          document.dispatchEvent(new CustomEvent('dialog:force-close:m-cart-drawer'));
        }
      };
      dialog.addEventListener('click', this.handleCloseClick);
    }
  }

  setupFreeSampleButtons() {
    const addButtons = document.querySelectorAll('[data-add-sample]');
    addButtons.forEach((button) => {
      // Avoid binding multiple listeners to the same button when the cart
      // re-renders. Use a data-flag instead of removing anonymous listeners.
      if (button.dataset.sampleListenerBound === 'true') return;

      button.addEventListener('click', (e) => this.handleAddSample(e));
      button.dataset.sampleListenerBound = 'true';
    });
  }

  async handleAddSample(event) {
    const button = event.currentTarget;
    const item = button.closest('[data-product-id]');
    if (!item) return;

    const productId = item.dataset.productId;
    const variantId = item.dataset.variantId;
    const sampleType = item.dataset.sampleType;
    const maxQuantity = parseInt(item.dataset.maxQuantity) || 1;

    this.debugLog('handleAddSample: click', {
      productId,
      variantId,
      sampleType,
      maxQuantity,
    });

    // Disable button and show loading spinner (same as default theme)
    button.disabled = true;
    button.classList.add('loading');

    try {
      const cartItems = document.querySelector('cart-items#m-cart-drawer-items');
      
      // Use FormData like ProductForm for better performance (includes sections in response)
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      
      if (cartItems) {
        const sections = cartItems.getSectionsToRender().map((section) => section.section).filter(Boolean);
        if (sections.length > 0) {
          formData.append('sections', sections.join(','));
        }
        formData.append('sections_url', window.location.pathname);
      }

      // Add to cart using FormData (like ProductForm - faster, includes sections)
      const addResponse = await fetch(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      if (!addResponse.ok) {
        throw new Error('Failed to add item to cart');
      }

      const response = await addResponse.json();

      // Check if Shopify returned an error
      if (response.status) {
        throw new Error(response.description || response.message || 'Failed to add item');
      }

      // Check limits using response items (if available) or fetch cart
      const productHandle = item.dataset.productHandle;
      let existingCount = 0;
      let cartData = null;

      // Try to use response.items first (if FormData returns it)
      if (response.items && Array.isArray(response.items)) {
        cartData = { items: response.items };
      } else {
        // Fallback: fetch cart.js for limit checking
        const cartResponse = await fetch(`${routes.cart_url}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (cartResponse.ok) {
          cartData = await cartResponse.json();
        }
      }

      // Check limits
      if (cartData && cartData.items) {
        cartData.items.forEach((cartItem) => {
          const matchesHandle = productHandle && cartItem.handle === productHandle;
          const matchesId = cartItem.product_id == productId;
          if (matchesHandle || matchesId) {
            existingCount += cartItem.quantity;
          }
        });
      }

      // If limit exceeded, remove the item and show error
      if (existingCount > maxQuantity && cartData && cartData.items) {
        this.debugLog('handleAddSample: limit exceeded after add, removing item', {
          productId,
          sampleType,
          existingCount,
          maxQuantity,
        });

        // Remove the item we just added
        if (cartItems) {
          const addedItem = cartData.items.find((cartItem) => 
            (productHandle && cartItem.handle === productHandle) || cartItem.product_id == productId
          );
          
          if (addedItem) {
            const lineIndex = cartData.items.indexOf(addedItem) + 1;
            await cartItems.updateQuantity(lineIndex, 0);
          }
        }

        this.showMessage(`You can only add ${maxQuantity} ${sampleType.replace('free-', '')}(s) per checkout.`, 'error');
        button.disabled = false;
        button.classList.remove('loading');
        return;
      }

      this.debugLog('handleAddSample: add success', {
        productId,
        variantId,
        sampleType,
        responseHasSections: !!response.sections,
      });

      // Use response sections directly (like ProductForm) - NO extra fetch!
      if (cartItems && response.sections) {
        cartItems.renderContents(response);
      } else if (cartItems) {
        // Fallback: refresh if sections not in response
        await cartItems.refreshCart();
      }

      // Trigger cart update event (this will re-init buttons after DOM update)
      document.dispatchEvent(new CustomEvent('cart:update'));

      // Don't show success message - the UI update (item disappearing from chooser) is the feedback
      // This also avoids showing "success" if the refresh actually failed
      
    } catch (error) {
      console.error('Error adding sample:', error);
      this.showMessage(error.message || 'Failed to add sample. Please try again.', 'error');
      button.disabled = false;
      button.classList.remove('loading');
    }
  }

  /**
   * Recently Viewed — hydrate the empty grid rendered by
   * snippets/m-cart-recently-viewed.liquid with cached cart-card HTML
   * from localStorage (theme:recently-viewed, populated on each PDP visit
   * by recently-viewed-products-cache.js).
   *
   * Hides products already in the cart so the chooser only shows actionable
   * cards (mirrors the freebie chooser behaviour).
   */
  async setupRecentlyViewed() {
    const section = document.querySelector('[data-recently-viewed-section]');
    if (!section) return;
    if (section.dataset.recentlyViewedHydrated === 'true') return;
    section.dataset.recentlyViewedHydrated = 'true';

    const grid = section.querySelector('[data-recently-viewed-grid]');
    if (!grid) return;

    const raw = localStorage.getItem('theme:recently-viewed');
    const list = (() => {
      try { return JSON.parse(raw) || []; } catch { return []; }
    })();
    if (!list.length) return;

    let cartHandles = new Set();
    try {
      const res = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (res.ok) {
        const cart = await res.json();
        cartHandles = new Set((cart.items || []).map((i) => i.handle));
      }
    } catch (e) {
      // If cart fetch fails, fall through and show all cached items.
    }

    const isHorizontal = section.dataset.layout === 'horizontal';
    const limit = parseInt(section.dataset.limit, 10) || 4;
    const shopifySection = section.closest('[id^="shopify-section-"]');
    const sectionId = shopifySection ? shopifySection.id : 'section';
    let inserted = 0;

    const refreshTargets = [];
    list.forEach((item, idx) => {
      if (!item || !item.cartCardElement) return;
      if (cartHandles.has(item.handle)) return;
      if (inserted >= limit) return;

      let card;
      if (isHorizontal) {
        const li = document.createElement('li');
        li.className = 'slider-component__item';
        li.id = `Slide-recently-viewed-${sectionId}-${idx}`;
        li.innerHTML = item.cartCardElement;
        grid.appendChild(li);
        card = li.querySelector('.routine-builder-product');
      } else {
        const wrap = document.createElement('div');
        wrap.innerHTML = item.cartCardElement;
        card = wrap.querySelector('.routine-builder-product');
        while (wrap.firstChild) grid.appendChild(wrap.firstChild);
      }
      if (card) refreshTargets.push({ handle: item.handle, card });
      inserted += 1;
    });

    if (inserted > 0) {
      section.removeAttribute('hidden');

      // Horizontal: wire up the manually-rendered prev/next arrows. The
      // snippet uses a plain <div class="slider-component"> (not the
      // <slider-component> custom element) to avoid the constructor running
      // with an empty <ul>. So we attach our own scroll-by-page handlers.
      if (isHorizontal) {
        this.setupRecentlyViewedSliderArrows(section);
      }

      // Newly-inserted cards need their ATC click handler bound.
      this.setupRecentlyViewedAdd();

      // The cached card HTML froze the price in whatever currency was active
      // when the product was last viewed. Re-render each card server-side in
      // the current market so the price reflects the active presentment
      // currency (a localStorage snapshot can't do cross-market conversion).
      this.refreshRecentlyViewedPrices(refreshTargets);
    }
  }

  /**
   * Swap each recently-viewed card's frozen price for a fresh server-rendered
   * one in the active market's presentment currency. Fetches the single-card
   * section (sections/recently-viewed-card.liquid) per handle; on any failure
   * the cached price is left in place.
   */
  async refreshRecentlyViewedPrices(targets) {
    if (!targets || !targets.length) return;
    const root = (window.routes && window.routes.root_url) || '/';
    const base = root.endsWith('/') ? root : `${root}/`;

    await Promise.all(
      targets.map(async ({ handle, card }) => {
        if (!card) return;
        const removeCard = () => (card.closest('li') || card).remove();
        try {
          const res = await fetch(`${base}products/${handle}?section_id=recently-viewed-card`, {
            headers: { Accept: 'text/html' },
          });
          // Draft/deleted product — 404 on the storefront — drop the card.
          if (!res.ok) { removeCard(); return; }
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('.routine-builder-product');
          // Now unavailable — the card snippet renders empty — drop the card.
          if (!fresh) { removeCard(); return; }
          const freshPrice = fresh.querySelector('.routine-builder-product__price');
          const livePrice = card.querySelector('.routine-builder-product__price');
          if (freshPrice && livePrice) livePrice.replaceWith(freshPrice);
        } catch (e) {
          // Network error — keep the cached card as-is.
        }
      })
    );

    // Hide the whole rail if every card got pruned.
    const section = document.querySelector('[data-recently-viewed-section]');
    const grid = section && section.querySelector('[data-recently-viewed-grid]');
    if (section && grid && !grid.querySelector('.routine-builder-product')) {
      section.setAttribute('hidden', '');
    }
  }

  // On cart:update, drop recently-viewed cards now in the cart and hide the rail if empty (hydrates once, no re-render).
  async pruneRecentlyViewedInCart() {
    const section = document.querySelector('[data-recently-viewed-section]');
    const grid = section && section.querySelector('[data-recently-viewed-grid]');
    if (!grid) return;

    let cartHandles = new Set();
    try {
      const res = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      cartHandles = new Set((cart.items || []).map((i) => i.handle));
    } catch (e) {
      return;
    }

    grid.querySelectorAll('.routine-builder-product').forEach((card) => {
      if (cartHandles.has(card.dataset.productHandle)) (card.closest('li') || card).remove();
    });

    if (!grid.querySelector('.routine-builder-product')) section.setAttribute('hidden', '');
  }

  setupRecentlyViewedSliderArrows(section) {
    const sliderWrap = section.querySelector('[data-recently-viewed-slider]');
    if (!sliderWrap) return;
    const slider = sliderWrap.querySelector('.slider-component__slider');
    const prev = sliderWrap.querySelector('[data-direction="previous"]');
    const next = sliderWrap.querySelector('[data-direction="next"]');
    if (!slider || !prev || !next) return;
    if (sliderWrap.dataset.arrowsBound === 'true') return;
    sliderWrap.dataset.arrowsBound = 'true';

    const updateArrows = () => {
      // Skip while layout hasn't been computed (e.g. cart drawer dialog is
      // still display:none). clientWidth/scrollWidth would both be 0 and the
      // math would falsely flag the next button as "at end".
      if (!slider.clientWidth || !slider.scrollWidth) return;
      const atStart = slider.scrollLeft <= 1;
      const atEnd = slider.scrollWidth > slider.clientWidth
        ? slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 1
        : true;
      if (atStart) prev.setAttribute('aria-disabled', 'true');
      else prev.removeAttribute('aria-disabled');
      if (atEnd) next.setAttribute('aria-disabled', 'true');
      else next.removeAttribute('aria-disabled');
    };

    prev.addEventListener('click', () => {
      slider.scrollBy({ left: -slider.clientWidth, behavior: 'smooth' });
    });
    next.addEventListener('click', () => {
      slider.scrollBy({ left: slider.clientWidth, behavior: 'smooth' });
    });
    slider.addEventListener('scroll', updateArrows, { passive: true });

    // Re-run when the slider gains/loses size — happens when the cart drawer
    // dialog opens and finally computes layout.
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => updateArrows());
      ro.observe(slider);
    }
    requestAnimationFrame(updateArrows);
  }

  setupRecentlyViewedAdd() {
    const buttons = document.querySelectorAll('[data-add-recent]');
    buttons.forEach((button) => {
      if (button.dataset.recentListenerBound === 'true') return;
      button.addEventListener('click', (e) => this.handleAddRecent(e));
      button.dataset.recentListenerBound = 'true';
    });
  }

  async handleAddRecent(event) {
    const button = event.currentTarget;
    const item = button.closest('[data-product-id]');
    if (!item) return;

    const variantId = item.dataset.variantId;
    if (!variantId) return;

    // Spinner only (no `disabled` — that greys the button). `.button.loading`
    // sets pointer-events:none, which blocks double-clicks. Matches bestsellers.
    if (button.classList.contains('loading')) return;
    button.classList.add('loading');

    try {
      const cartItems = document.querySelector('cart-items#m-cart-drawer-items');
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      if (cartItems) {
        const sections = cartItems.getSectionsToRender().map((s) => s.section).filter(Boolean);
        if (sections.length > 0) formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const addResponse = await fetch(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });

      if (!addResponse.ok) throw new Error('Failed to add item to cart');
      const response = await addResponse.json();
      if (response.status) throw new Error(response.description || response.message || 'Failed to add item');

      this.debugLog('handleAddRecent: add success', { variantId, responseHasSections: !!response.sections });

      if (cartItems && response.sections) {
        cartItems.renderContents(response);
      } else if (cartItems) {
        await cartItems.refreshCart();
      }

      document.dispatchEvent(new CustomEvent('cart:update'));

      // Recently-viewed lives outside the re-render region, so drop the added item ourselves.
      item.closest('li')?.remove();
    } catch (error) {
      console.error('Error adding from recently viewed:', error);
      this.showMessage(error.message || 'Failed to add. Please try again.', 'error');
    } finally {
      button.classList.remove('loading');
    }
  }

  updateFreeShippingBar() {
    // Wait a bit for DOM to update after cart refresh
    setTimeout(() => {
      const freeShippingBar = document.querySelector('[data-free-shipping-bar]');
      if (!freeShippingBar) return;

      // Free shipping bar is always visible - Liquid handles empty vs filled states
      // (empty shows "Add items to qualify", filled shows progress or success)
      freeShippingBar.style.display = '';

      // Progress bar is updated via Liquid, but we can add animations here if needed
      const progressBar = freeShippingBar.querySelector('[data-progress-bar]');
      if (progressBar) {
        progressBar.style.transition = 'width 0.3s ease';
      }
    }, 200);
  }

  setupCartUpdateListener() {
    document.addEventListener('cart:update', () => {
      // Re-initialize free sample buttons and accordions after cart update
      setTimeout(() => {
        this.setupFreeSampleButtons();
        this.setupCartDrawerAccordions();
        this.setupRecentlyViewed();
        this.setupRecentlyViewedAdd();
        this.pruneRecentlyViewedInCart();
        this.updateFreeShippingBar();
        this.removeDuplicateLoyaltyElements();
      }, 300);
    });
    
    // Also listen for when cart-items element updates
    const cartItems = document.querySelector('cart-items#m-cart-drawer-items');
    if (cartItems) {
      const observer = new MutationObserver(() => {
        setTimeout(() => {
          this.setupCartDrawerAccordions();
          this.updateFreeShippingBar();
          this.removeDuplicateLoyaltyElements();
        }, 100);
      });
      
      observer.observe(cartItems, {
        childList: true,
        subtree: true
      });
    }

    // Watch for loyalty program elements being added to footer
    const footer = this.element?.querySelector('[data-cart-footer]') || document.querySelector('[data-cart-footer]');
    if (footer) {
      const loyaltyObserver = new MutationObserver(() => {
        setTimeout(() => {
          this.removeDuplicateLoyaltyElements();
        }, 100);
      });
      
      loyaltyObserver.observe(footer, {
        childList: true,
        subtree: true
      });
    }
  }

  showMessage(message, type = 'info') {
    // Create a temporary message element
    const messageEl = document.createElement('div');
    messageEl.className = `m-cart-drawer__message m-cart-drawer__message--${type}`;
    messageEl.textContent = message;
    messageEl.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 1rem 1.5rem;
      background: ${type === 'error' ? '#f44336' : '#4caf50'};
      color: white;
      border-radius: 4px;
      z-index: 10000;
      animation: slideIn 0.3s ease;
    `;

    document.body.appendChild(messageEl);

    setTimeout(() => {
      messageEl.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => messageEl.remove(), 300);
    }, 3000);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const mCartDrawer = document.querySelector('.m-cart-drawer__inner') || document.querySelector('#MCartDrawer');
    if (mCartDrawer) {
      new MCartDrawer(mCartDrawer);
    }
  });
} else {
  const mCartDrawer = document.querySelector('.m-cart-drawer__inner') || document.querySelector('#MCartDrawer');
  if (mCartDrawer) {
    new MCartDrawer(mCartDrawer);
  }
}

// Also listen for section loads in theme editor
document.addEventListener('shopify:section:load', (event) => {
  const mCartDrawer = document.querySelector('.m-cart-drawer__inner') || document.querySelector('#MCartDrawer');
  if (mCartDrawer) {
    new MCartDrawer(mCartDrawer);
  }
});
